// src/stores/schedulerStore.ts
// ============================================
// SchedulerStore - Hermes 任务调度状态管理
//
// 职责：
//   1. 维护调度任务列表 / 执行历史 / 加载状态
//   2. 通过 RuntimeManager 调用 SchedulerRuntime 的 List/GetHistory/Trigger/Pause/Resume
//   3. 提供 Hermes 节奏任务过滤（getHermesTasks / getOtherTasks）
//   4. 浏览器环境降级：RuntimeManager 不可用时返回空数组，不抛错
//
// 依赖：Zustand（项目已依赖）
// ============================================

import { create } from "zustand";
import RuntimeManager from "../runtime/RuntimeManager";
import { RuntimeName } from "../types/runtime";
import {
  SchedulerQuery,
  SchedulerCommand,
} from "../runtime/SchedulerRuntime/types";
import type {
  ScheduledTask,
  TaskExecutionRecord,
} from "../runtime/SchedulerRuntime/types";

// Hermes 节奏任务 ID 前缀
const HERMES_RHYTHM_PREFIX = "builtin.hermes-rhythm-";

// ============================================
// 类型定义
// ============================================

export interface SchedulerState {
  /** 所有调度任务 */
  tasks: ScheduledTask[];
  /** 执行历史（按时间倒序） */
  history: TaskExecutionRecord[];
  /** 加载状态 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 最后更新时间（ISO 字符串） */
  lastUpdated: string | null;
  /** 自动刷新开关 */
  autoRefresh: boolean;
  /** 正在触发中的任务 ID 列表（用于 UI 显示 running 状态） */
  triggeringTaskIds: string[];

  // ===== Actions =====
  /** 拉取所有调度任务 */
  fetchTasks: () => Promise<void>;
  /** 拉取执行历史（可按 taskId 过滤） */
  fetchHistory: (taskId?: string) => Promise<void>;
  /** 手动触发任务 */
  triggerTask: (taskId: string) => Promise<void>;
  /** 暂停任务 */
  pauseTask: (taskId: string) => Promise<void>;
  /** 恢复任务 */
  resumeTask: (taskId: string) => Promise<void>;
  /** 清空本地展示的执行历史（服务端历史保留） */
  clearHistory: () => void;
  /** 设置自动刷新开关 */
  setAutoRefresh: (on: boolean) => void;
  /** 返回过滤后的 Hermes 节奏任务 */
  getHermesTasks: () => ScheduledTask[];
  /** 返回非 Hermes 节奏的内置任务 */
  getOtherTasks: () => ScheduledTask[];
}

// ============================================
// 工具函数
// ============================================

/**
 * 判断 RuntimeManager 是否可用
 * 浏览器环境降级：不可用时返回 false，调用方据此返回空数据
 */
function isRuntimeAvailable(): boolean {
  try {
    return (
      !!RuntimeManager &&
      typeof RuntimeManager.query === "function" &&
      typeof RuntimeManager.execute === "function"
    );
  } catch {
    return false;
  }
}

/**
 * 从 RuntimeResponse 中提取调度数据数组
 * 兼容两层包装：
 *   - 情况 1：resp.data 直接是数组（Dispatcher 已解包）
 *   - 情况 2：resp.data 是 SchedulerResult，内层 data 才是数组
 */
function extractArray<T>(resp: any): T[] {
  if (!resp || !resp.success) return [];
  const data = resp.data;
  // 情况 1：data 直接是数组
  if (Array.isArray(data)) return data as T[];
  // 情况 2：data 是 SchedulerResult 包装
  if (data && typeof data === "object") {
    if (data.success === false) return [];
    if (Array.isArray(data.data)) return data.data as T[];
  }
  return [];
}

// ============================================
// Store 实现
// ============================================

export const useSchedulerStore = create<SchedulerState>((set, get) => ({
  tasks: [],
  history: [],
  loading: false,
  error: null,
  lastUpdated: null,
  autoRefresh: true,
  triggeringTaskIds: [],

  fetchTasks: async () => {
    // 浏览器环境降级
    if (!isRuntimeAvailable()) {
      set({
        tasks: [],
        loading: false,
        error: null,
        lastUpdated: new Date().toISOString(),
      });
      return;
    }
    // 仅在首次加载（tasks 为空）时显示 loading，避免轮询时 UI 闪烁
    if (get().tasks.length === 0) {
      set({ loading: true });
    }
    try {
      const resp = await RuntimeManager.query(RuntimeName.Scheduler, {
        type: SchedulerQuery.List,
      });
      const tasks = extractArray<ScheduledTask>(resp);
      set({
        tasks,
        loading: false,
        error: null,
        lastUpdated: new Date().toISOString(),
      });
    } catch (err) {
      set({
        loading: false,
        error: err instanceof Error ? err.message : String(err),
        lastUpdated: new Date().toISOString(),
      });
    }
  },

  fetchHistory: async (taskId?: string) => {
    if (!isRuntimeAvailable()) {
      set({
        history: [],
        error: null,
        lastUpdated: new Date().toISOString(),
      });
      return;
    }
    try {
      const resp = await RuntimeManager.query(RuntimeName.Scheduler, {
        type: SchedulerQuery.GetHistory,
        taskId,
      });
      const history = extractArray<TaskExecutionRecord>(resp);
      set({
        history,
        error: null,
        lastUpdated: new Date().toISOString(),
      });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : String(err),
        lastUpdated: new Date().toISOString(),
      });
    }
  },

  triggerTask: async (taskId: string) => {
    if (!isRuntimeAvailable()) return;
    // 标记为触发中（UI 显示 running 状态）
    set((s) => ({
      triggeringTaskIds: [...s.triggeringTaskIds, taskId],
      error: null,
    }));
    try {
      await RuntimeManager.execute(
        RuntimeName.Scheduler,
        SchedulerCommand.Trigger,
        { taskId }
      );
      // 触发后刷新任务列表 + 历史
      await Promise.all([get().fetchTasks(), get().fetchHistory()]);
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      // 移除触发中标记
      set((s) => ({
        triggeringTaskIds: s.triggeringTaskIds.filter((id) => id !== taskId),
      }));
    }
  },

  pauseTask: async (taskId: string) => {
    if (!isRuntimeAvailable()) return;
    try {
      await RuntimeManager.execute(
        RuntimeName.Scheduler,
        SchedulerCommand.Pause,
        { taskId }
      );
      await get().fetchTasks();
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : String(err),
      });
    }
  },

  resumeTask: async (taskId: string) => {
    if (!isRuntimeAvailable()) return;
    try {
      await RuntimeManager.execute(
        RuntimeName.Scheduler,
        SchedulerCommand.Resume,
        { taskId }
      );
      await get().fetchTasks();
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : String(err),
      });
    }
  },

  clearHistory: () => {
    // 仅清空本地展示的日志（服务端历史保留）
    set({ history: [] });
  },

  setAutoRefresh: (on: boolean) => {
    set({ autoRefresh: on });
  },

  getHermesTasks: () => {
    return get().tasks.filter((t) => t.id.startsWith(HERMES_RHYTHM_PREFIX));
  },

  getOtherTasks: () => {
    return get().tasks.filter((t) => !t.id.startsWith(HERMES_RHYTHM_PREFIX));
  },
}));

export default useSchedulerStore;
